import { Form, Button, Checkbox } from "antd"
import { CustomForm } from "ui/Form"
import { Card } from "ui/Card"
import { PlusOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { Text } from "ui/Typography"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { SeasonTypeSelector } from "./SeasonTypeSelector"
import { DateRangeBlock } from "./DateRangeBlock"
import { SEASON_TYPES } from "./utils/constants"
import { DaysAndHours } from "./DaysAndHours"
import { modelSelector } from "store/selectors"
import { getGuideTrip, updateTrip } from "store/trips"
import { routes } from "router"
import { serialize } from "object-to-formdata"
import dayjs from "dayjs"
import { useState, useEffect } from "react"

export const ListingScheduling = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const [form] = Form.useForm()
  const trip = useSelector(modelSelector("trip"))
  const [isSeasonalExperience, setIsSeasonalExperience] = useState(false)
  const [timeBlocks, setTimeBlocks] = useState([0])

  useEffect(() => {
    if (id) {
      dispatch(getGuideTrip(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if ("seasonal" in trip) {
      setIsSeasonalExperience(trip.seasonal)
      if (trip.available_date_ranges?.length > 0) {
        setTimeBlocks(Array.from({ length: trip.available_date_ranges.length }, (_, i) => i))
      }
    }
  }, [trip, trip?.seasonal, trip?.available_date_ranges])

  const handleSeasonChange = (e) => {
    setIsSeasonalExperience(e.target.value === SEASON_TYPES.SEASONAL)
  }

  const handleAddTimeBlock = () => {
    setTimeBlocks((prevBlocks) => [...prevBlocks, prevBlocks.length])
  }

  const handleRemoveTimeBlock = (index) => {
    setTimeBlocks((prevBlocks) => prevBlocks.filter((blockIndex) => blockIndex !== index))
  }

  const prepareUpdateData = async (validateForm = false) => {
    const values = validateForm ? await form.validateFields() : form.getFieldsValue()

    const updateData = {}

    if (!isSeasonalExperience && trip?.seasonal) {
      updateData.seasonal = false
    } else if (isSeasonalExperience) {
      updateData.seasonal = true
      updateData.recurring_yearly = values.recurringYearly

      const dateRanges = timeBlocks
        .map((_, index) => {
          const startDate = values[`seasonStart_${index}`]
          const endDate = values[`seasonEnd_${index}`]
          if (startDate && endDate) {
            return {
              start: startDate.format("YYYY-MM-DD"),
              end: endDate.format("YYYY-MM-DD")
            }
          }
          return null
        })
        .filter(Boolean)

      if (dateRanges.length > 0) {
        updateData.available_date_ranges = dateRanges
      }
    }

    return updateData
  }

  const onFinish = async () => {
    try {
      const updateData = await prepareUpdateData(true)
      const formData = serialize({ trip: updateData }, { allowEmptyArrays: true })

      await dispatch(updateTrip(id, formData))
      navigate(routes.proNewListingMediaPath({ id }))
    } catch (error) {
      console.error("Form validation failed:", error)
    }
  }

  const handleSaveDraftAndExit = async () => {
    try {
      const updateData = await prepareUpdateData(false)
      const formData = serialize({ trip: updateData }, { allowEmptyArrays: true })

      await dispatch(updateTrip(id, formData))
      navigate(routes.proExperiencesPath())
    } catch (error) {
      console.error("Error saving draft:", error)
    }
  }

  const handleNext = async () => {
    await onFinish()
  }

  const handlePrevious = () => {
    navigate(routes.proNewListingDetailsPath({ id }))
  }

  return (
    <>
      <CustomForm form={form} layout="vertical" autoComplete="off" onFinish={handleNext} initialValues={trip}>
        <Card className="mb-3xl" title={t("listings.tabs.scheduling.sections.seasons.title")}>
          <SeasonTypeSelector onChange={handleSeasonChange} value={isSeasonalExperience ? SEASON_TYPES.SEASONAL : SEASON_TYPES.ALL_YEAR} />
          {isSeasonalExperience && (
            <>
              {timeBlocks.map((blockIndex) => {
                const dateRange = trip?.available_date_ranges?.[blockIndex]
                return (
                  <DateRangeBlock
                    key={blockIndex}
                    blockIndex={blockIndex}
                    onDelete={handleRemoveTimeBlock}
                    isFirstBlock={blockIndex === 0}
                    isSeasonalActive={isSeasonalExperience}
                    initialStartDate={dateRange?.start ? dayjs(dateRange.start) : undefined}
                    initialEndDate={dateRange?.end ? dayjs(dateRange.end) : undefined}
                  />
                )
              })}
              <Button type="link" icon={<PlusOutlined />} onClick={handleAddTimeBlock} className="mt-m pl-0">
                {t("listings.tabs.scheduling.sections.seasons.add_time_block")}
              </Button>
              <Form.Item
                name="recurringYearly"
                valuePropName="checked"
                className="mt-l"
                initialValue={trip?.available_date_ranges?.length > 0 ? trip.recurring_yearly : true}
              >
                <Checkbox>{t("listings.tabs.scheduling.sections.seasons.recurring_yearly")}</Checkbox>
              </Form.Item>
            </>
          )}
        </Card>
        <DaysAndHours form={form} />
      </CustomForm>
      <ListingActionButtons onSaveDraftAndExit={handleSaveDraftAndExit} onNext={handleNext} onPrevious={handlePrevious} form={form} />
    </>
  )
}
